import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
    QrCode, MapPin, MagnifyingGlass, NavigationArrow, DownloadSimple,
    Spinner, Copy, Check, ArrowSquareOut
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const pinIcon = L.divIcon({
    className: '',
    html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;background:#22c55e;border:3px solid #0f172a;box-shadow:0 2px 6px rgba(0,0,0,.35);transform:rotate(-45deg);"></div>`,
    iconSize: [30, 30], iconAnchor: [15, 30],
});

function ClickCapture({ onPick }) {
    useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
    return null;
}

function Recenter({ center }) {
    const map = useMap();
    if (center) map.setView(center, Math.max(map.getZoom(), 11), { animate: true });
    return null;
}

export default function QRActivationTool() {
    const [pin, setPin] = useState(null); // {lat, lng}
    const [place, setPlace] = useState('');
    const [baseUrl, setBaseUrl] = useState(window.location.origin);
    const [search, setSearch] = useState('');
    const [searching, setSearching] = useState(false);
    const [geoLoading, setGeoLoading] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [copied, setCopied] = useState(false);
    const posterRef = useRef(null);

    const buildUrl = useCallback((lat, lng, placeName) => {
        const p = new URLSearchParams();
        p.set('lat', Number(lat).toFixed(5));
        p.set('lng', Number(lng).toFixed(5));
        if (placeName) p.set('place', placeName);
        return `${baseUrl.replace(/\/$/, '')}/adventure?${p.toString()}`;
    }, [baseUrl]);

    const pickLocation = useCallback(async (lat, lng) => {
        setPin({ lat, lng });
        setQrDataUrl(null);
        try {
            const res = await axios.get(`${API}/public/reverse-geocode`, { params: { lat, lng } });
            setPlace(res.data?.place || res.data?.display_name || '');
        } catch (e) { /* keep manual place */ }
    }, []);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!search.trim()) return;
        setSearching(true);
        try {
            const res = await axios.get(`${API}/public/geocode`, { params: { q: search.trim() } });
            const r = Array.isArray(res.data) ? res.data[0] : res.data;
            const lat = r?.lat ?? r?.latitude;
            const lng = r?.lng ?? r?.lon ?? r?.longitude;
            if (lat == null || lng == null) { toast.error('Location not found'); return; }
            await pickLocation(parseFloat(lat), parseFloat(lng));
        } catch (err) {
            toast.error('Could not find that place');
        } finally { setSearching(false); }
    };

    const useMyLocation = () => {
        if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => { pickLocation(pos.coords.latitude, pos.coords.longitude); setGeoLoading(false); },
            () => { toast.error('Location permission denied'); setGeoLoading(false); },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const generate = async () => {
        if (!pin) { toast.error('Drop a pin on the map first'); return; }
        const url = buildUrl(pin.lat, pin.lng, place);
        try {
            const dataUrl = await QRCode.toDataURL(url, {
                width: 480, margin: 1, errorCorrectionLevel: 'M',
                color: { dark: '#0f172a', light: '#ffffff' },
            });
            setQrDataUrl(dataUrl);
        } catch (e) { toast.error('QR generation failed'); }
    };

    const currentUrl = pin ? buildUrl(pin.lat, pin.lng, place) : '';

    const copyUrl = async () => {
        if (!currentUrl) return;
        await navigator.clipboard.writeText(currentUrl);
        setCopied(true); setTimeout(() => setCopied(false), 1500);
    };

    const downloadPoster = async () => {
        if (!qrDataUrl) { toast.error('Generate the QR first'); return; }
        const W = 900, H = 1200;
        const canvas = document.createElement('canvas');
        canvas.width = W; canvas.height = H;
        const ctx = canvas.getContext('2d');
        // background
        ctx.fillStyle = '#fefce8'; ctx.fillRect(0, 0, W, H);
        // outer border
        ctx.strokeStyle = '#0f172a'; ctx.lineWidth = 16;
        ctx.strokeRect(24, 24, W - 48, H - 48);
        // header band
        ctx.fillStyle = '#22c55e'; ctx.fillRect(24, 24, W - 48, 150);
        ctx.strokeRect(24, 24, W - 48, 150);
        ctx.fillStyle = '#ffffff';
        ctx.font = "bold 64px 'Fredoka', sans-serif";
        ctx.textAlign = 'center';
        ctx.fillText('BUSH RANGERS', W / 2, 122);
        // subtitle
        ctx.fillStyle = '#0f172a';
        ctx.font = "bold 46px 'Fredoka', sans-serif";
        ctx.fillText('Scan to Start Your', W / 2, 270);
        ctx.fillText('Bush Adventure! 🌿', W / 2, 330);
        // QR box
        const qrSize = 520; const qx = (W - qrSize) / 2; const qy = 390;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(qx - 20, qy - 20, qrSize + 40, qrSize + 40);
        ctx.lineWidth = 10; ctx.strokeStyle = '#0f172a';
        ctx.strokeRect(qx - 20, qy - 20, qrSize + 40, qrSize + 40);
        const img = new Image();
        img.onload = () => {
            ctx.drawImage(img, qx, qy, qrSize, qrSize);
            // place name
            ctx.fillStyle = '#0f172a';
            ctx.font = "bold 44px 'Fredoka', sans-serif";
            const label = place ? `📍 ${place}` : '📍 Explore your local wildlife';
            const trimmed = label.length > 42 ? label.slice(0, 41) + '…' : label;
            ctx.fillText(trimmed, W / 2, qy + qrSize + 90);
            // footer
            ctx.fillStyle = '#475569';
            ctx.font = "28px 'Fredoka', sans-serif";
            ctx.fillText('Discover habitats, wildlife & missions near you', W / 2, qy + qrSize + 150);
            ctx.font = "bold 26px 'Fredoka', sans-serif";
            ctx.fillStyle = '#16a34a';
            ctx.fillText(baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') + '/adventure', W / 2, H - 70);
            // download
            const link = document.createElement('a');
            const slug = (place || 'activation').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
            link.download = `bushrangers-qr-${slug}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            toast.success('Poster downloaded!');
        };
        img.src = qrDataUrl;
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="qr-activation-tool">
            <div className="flex items-center gap-2 mb-2">
                <QrCode weight="duotone" className="text-[#22c55e]" size={32} />
                <h1 className="font-fredoka text-2xl font-bold text-[#1e293b]">Activation QR Codes</h1>
            </div>
            <p className="text-sm text-[#475569] mb-6 max-w-2xl">
                Drop a pin on an activation site (or search a suburb / use your location). We generate a QR poster
                that links straight to the public Adventure page pre-loaded with that location.
            </p>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Left: map + controls */}
                <div className="space-y-4">
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search suburb, postcode or park…"
                            className="flex-1 px-4 py-3 rounded-xl border-4 border-[#0f172a] font-semibold text-sm focus:outline-none"
                            data-testid="qr-search-input"
                        />
                        <button
                            type="submit"
                            disabled={searching}
                            className="px-4 py-3 rounded-xl border-4 border-[#0f172a] bg-[#22c55e] text-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-50"
                            data-testid="qr-search-btn"
                        >
                            {searching ? <Spinner className="animate-spin" size={20} /> : <MagnifyingGlass weight="bold" size={20} />}
                        </button>
                        <button
                            type="button"
                            onClick={useMyLocation}
                            disabled={geoLoading}
                            className="px-4 py-3 rounded-xl border-4 border-[#0f172a] bg-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-50"
                            data-testid="qr-geo-btn"
                            title="Use my location"
                        >
                            {geoLoading ? <Spinner className="animate-spin" size={20} /> : <NavigationArrow weight="bold" size={20} />}
                        </button>
                    </form>

                    <div className="rounded-2xl border-4 border-[#0f172a] overflow-hidden shadow-[4px_4px_0px_#0f172a]" style={{ height: 360 }}>
                        <MapContainer center={[-25.27, 133.77]} zoom={4} style={{ height: '100%', width: '100%' }} data-testid="qr-map">
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                            <ClickCapture onPick={pickLocation} />
                            {pin && <Marker position={[pin.lat, pin.lng]} icon={pinIcon} />}
                            {pin && <Recenter center={[pin.lat, pin.lng]} />}
                        </MapContainer>
                    </div>
                    <p className="text-xs text-[#64748b] flex items-center gap-1">
                        <MapPin weight="fill" size={14} className="text-[#22c55e]" />
                        Tap anywhere on the map to place the activation pin.
                    </p>
                </div>

                {/* Right: details + QR */}
                <div className="space-y-4">
                    <div className="bg-white rounded-2xl border-4 border-[#0f172a] p-5 shadow-[4px_4px_0px_#0f172a] space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-[#0f172a] mb-1 uppercase tracking-wide">Place label (shown on poster)</label>
                            <input
                                value={place}
                                onChange={(e) => { setPlace(e.target.value); setQrDataUrl(null); }}
                                placeholder="e.g. Kings Park, Perth"
                                className="w-full px-4 py-2.5 rounded-xl border-3 border-[#0f172a] font-semibold text-sm focus:outline-none"
                                data-testid="qr-place-input"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-[#0f172a] mb-1 uppercase tracking-wide">Base URL (edit for production domain)</label>
                            <input
                                value={baseUrl}
                                onChange={(e) => { setBaseUrl(e.target.value); setQrDataUrl(null); }}
                                className="w-full px-4 py-2.5 rounded-xl border-3 border-[#0f172a] font-semibold text-sm focus:outline-none"
                                data-testid="qr-baseurl-input"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-[#0f172a] mb-1 uppercase tracking-wide">Coordinates</label>
                            <p className="text-sm font-mono text-[#475569]" data-testid="qr-coords">
                                {pin ? `${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}` : 'No pin placed yet'}
                            </p>
                        </div>
                        {currentUrl && (
                            <div>
                                <label className="block text-xs font-bold text-[#0f172a] mb-1 uppercase tracking-wide">Adventure link</label>
                                <div className="flex items-center gap-2">
                                    <p className="text-xs text-[#16a34a] break-all flex-1 font-semibold" data-testid="qr-url">{currentUrl}</p>
                                    <button onClick={copyUrl} className="shrink-0 p-2 rounded-lg border-2 border-[#0f172a]" data-testid="qr-copy-btn" title="Copy link">
                                        {copied ? <Check weight="bold" size={16} className="text-[#22c55e]" /> : <Copy weight="bold" size={16} />}
                                    </button>
                                    <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 p-2 rounded-lg border-2 border-[#0f172a]" title="Open link">
                                        <ArrowSquareOut weight="bold" size={16} />
                                    </a>
                                </div>
                            </div>
                        )}
                        <button
                            onClick={generate}
                            disabled={!pin}
                            className="w-full px-4 py-3 rounded-xl border-4 border-[#0f172a] bg-[#22c55e] text-white font-fredoka font-bold shadow-[3px_3px_0px_#0f172a] disabled:opacity-40 disabled:shadow-none hover:-translate-y-0.5 transition-transform"
                            data-testid="qr-generate-btn"
                        >
                            Generate QR Code
                        </button>
                    </div>

                    {qrDataUrl && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-2xl border-4 border-[#0f172a] p-5 shadow-[4px_4px_0px_#0f172a] text-center"
                            ref={posterRef}
                        >
                            <img src={qrDataUrl} alt="Activation QR" className="mx-auto w-52 h-52" data-testid="qr-preview" />
                            <p className="font-fredoka font-bold text-[#0f172a] mt-3">{place || 'Local Adventure'}</p>
                            <button
                                onClick={downloadPoster}
                                className="mt-4 w-full px-4 py-3 rounded-xl border-4 border-[#0f172a] bg-[#0f172a] text-white font-fredoka font-bold shadow-[3px_3px_0px_#22c55e] hover:-translate-y-0.5 transition-transform flex items-center justify-center gap-2"
                                data-testid="qr-download-btn"
                            >
                                <DownloadSimple weight="bold" size={20} />
                                Download Poster (PNG)
                            </button>
                        </motion.div>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
